import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import LeadsTabs from './LeadsTabs';
import {
  Card, Button, Badge, Modal, Tooltip, EmptyState, Spinner,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '../../components/UI';
import { smartListsAPI, usersAPI, leadsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus, HiOutlineSparkles, HiOutlinePencil, HiOutlineTrash,
  HiOutlineEye, HiOutlineUserAdd,
} from 'react-icons/hi';
import SmartListModal from './SmartListModal';

// Human-readable summary of a smart list's filters
const describeFilters = (filters = {}) => {
  const parts = [];
  if (filters.city) parts.push(`City: ${filters.city}`);
  if (filters.source) parts.push(`Source: ${filters.source}`);
  if (filters.status) parts.push(`Status: ${filters.status.replace(/_/g, ' ')}`);
  if (filters.stage) parts.push(`Stage: ${filters.stage.replace(/_/g, ' ')}`);
  if (filters.priority) parts.push(`Priority: ${filters.priority}`);
  if (filters.minLeadScore !== undefined || filters.maxLeadScore !== undefined) {
    const min = filters.minLeadScore ?? 0;
    const max = filters.maxLeadScore ?? 100;
    parts.push(`Score ${min}–${max}`);
  }
  if (Array.isArray(filters.tags) && filters.tags.length > 0) {
    parts.push(`Tags: ${filters.tags.join(', ')}`);
  }
  if (filters.assignment) parts.push(filters.assignment === 'unassigned' ? 'Unassigned' : 'Assigned');
  if (filters.lastContactedWithinDays !== undefined) parts.push(`Contacted ≤ ${filters.lastContactedWithinDays}d ago`);
  if (filters.neverContacted) parts.push('Never contacted');
  if (filters.nextFollowUpWithinDays !== undefined) parts.push(`Follow-up ≤ ${filters.nextFollowUpWithinDays}d`);
  if (filters.createdAfter || filters.createdBefore) {
    parts.push(`Created ${filters.createdAfter || '…'} → ${filters.createdBefore || 'now'}`);
  }
  if (filters.search) parts.push(`Search: "${filters.search}"`);
  return parts;
};

const STATUS_BADGES = {
  new: 'new', open: 'info', in_progress: 'primary', follow_up: 'follow_up',
  cold: 'cold', warm: 'warm', hot: 'hot', converted: 'converted',
  closed: 'closed', lost: 'danger', not_connected: 'not_connected',
};

const SmartLists = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [viewingList, setViewingList] = useState(null);

  // Fetch smart lists
  const { data: listsData, isLoading } = useQuery({
    queryKey: ['smartLists'],
    queryFn: async () => {
      const response = await smartListsAPI.getAll();
      return response.data?.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => smartListsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['smartLists']);
      toast.success('Smart list deleted successfully');
      setShowDeleteModal(false);
      setListToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete smart list');
    },
  });

  const smartLists = listsData || [];

  const handleAdd = () => {
    setSelectedList(null);
    setShowModal(true);
  };

  const handleEdit = (list) => {
    setSelectedList(list);
    setShowModal(true);
  };

  const handleDeleteClick = (list) => {
    setListToDelete(list);
    setShowDeleteModal(true);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Leads section tabs */}
        <LeadsTabs />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Smart Lists</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Dynamic lead groups based on city, source, status, score and activity — for targeted outreach
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlinePlus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            <span className="hidden sm:inline">New Smart List</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>

        {/* Smart Lists Grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : smartLists.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineSparkles className="w-8 h-8 text-gray-400" />}
              title="No smart lists yet"
              description="Create a smart list to group leads by filters like city, source, status, score or activity"
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {smartLists.map((list) => (
              <Card key={list._id} className="flex flex-col justify-between hover:shadow-lg transition-all duration-200">
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                        style={{ backgroundColor: `${list.color || '#3B82F6'}1A` }}
                      >
                        {list.icon || '🏷️'}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{list.name}</p>
                        <p className="text-xs text-gray-400">
                          {list.member_count ?? 0} lead{list.member_count === 1 ? '' : 's'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="primary" size="sm">Dynamic</Badge>
                  </div>

                  {list.description && (
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3">{list.description}</p>
                  )}

                  <div className="flex flex-wrap gap-1 mb-3">
                    {describeFilters(list.filters).slice(0, 4).map((part, i) => (
                      <Badge key={i} variant="default" size="sm">{part}</Badge>
                    ))}
                    {describeFilters(list.filters).length > 4 && (
                      <Badge variant="default" size="sm">+{describeFilters(list.filters).length - 4}</Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                  <Tooltip text="View leads">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<HiOutlineEye className="w-4 h-4 text-gray-500" />}
                      onClick={() => setViewingList(list)}
                      title="View leads"
                    />
                  </Tooltip>
                  <Tooltip text="Edit smart list">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                      onClick={() => handleEdit(list)}
                      title="Edit"
                    />
                  </Tooltip>
                  <Tooltip text="Delete smart list">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => handleDeleteClick(list)}
                      title="Delete"
                    />
                  </Tooltip>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create / Edit Modal */}
        <SmartListModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedList(null); }}
          smartList={selectedList}
        />

        {/* View Leads Modal */}
        <SmartListLeadsModal
          list={viewingList}
          onClose={() => setViewingList(null)}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Smart List"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{listToDelete?.name}</span>? Leads are not affected — only the saved filter group is removed.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => listToDelete && deleteMutation.mutate(listToDelete._id)}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

// ========================================
// Leads inside a smart list — with targeted outreach (bulk assign)
// ========================================
const SmartListLeadsModal = ({ list, onClose }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const pageSize = 20;

  const isOpen = Boolean(list?._id);

  const { data: leadsData, isLoading } = useQuery({
    queryKey: ['smartListLeads', list?._id, page],
    queryFn: async () => {
      const response = await smartListsAPI.getLeads(list._id, { page, limit: pageSize });
      return response.data || {};
    },
    enabled: isOpen,
  });

  // Telecallers for targeted outreach assignment
  const { data: telecallers } = useQuery({
    queryKey: ['telecallers'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: 'telecaller', limit: 200 });
      return response.data?.data || [];
    },
    enabled: isOpen,
  });

  const leads = leadsData?.data || [];
  const total = leadsData?.pagination?.total || 0;
  const totalPages = leadsData?.pagination?.totalPages || 1;

  const assignMutation = useMutation({
    mutationFn: (leadIds) => leadsAPI.bulkAssign({ leadIds, assignedTo: assignTo }),
    onSuccess: (response) => {
      const assigned = response.data?.data?.assignedCount ?? selectedIds.length;
      toast.success(`${assigned} lead${assigned === 1 ? '' : 's'} assigned successfully`);
      setSelectedIds([]);
      queryClient.invalidateQueries(['smartLists']);
      queryClient.invalidateQueries(['smartListLeads', list?._id]);
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to assign leads');
    },
  });

  const handleAssign = () => {
    if (!assignTo) {
      toast.error('Select a telecaller to assign to');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one lead');
      return;
    }
    assignMutation.mutate(selectedIds);
  };

  const allPageSelected = leads.length > 0 && leads.every((l) => selectedIds.includes(l._id));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !leads.some((l) => l._id === id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...leads.map((l) => l._id)])]);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const filterSummary = useMemo(() => describeFilters(list?.filters || []), [list]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={list ? `${list.name} — Leads` : 'Leads'} size="xl">
      <div className="space-y-4">
        {/* Outreach action bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-200">
          <span className="text-sm font-semibold text-secondary-700 flex-shrink-0">
            Targeted outreach:
          </span>
          {(telecallers || []).length === 0 ? (
            <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2 flex-1">
              No telecallers found in your organization. Add users with the{' '}
              <strong>Telecaller</strong> role in CRM → Users first.
            </p>
          ) : (
            <select
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              className="px-3 py-2 border border-secondary-200 rounded-lg text-sm bg-white flex-1 min-w-0"
            >
              <option value="">Select telecaller…</option>
              {(telecallers || []).map((u) => (
                <option key={u._id} value={u._id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </select>
          )}
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlineUserAdd className="w-4 h-4" />}
            onClick={handleAssign}
            loading={assignMutation.isPending}
            disabled={selectedIds.length === 0 || (telecallers || []).length === 0}
            className="flex-shrink-0"
          >
            Assign {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}selected
          </Button>
        </div>

        {filterSummary.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {filterSummary.map((part, i) => (
              <Badge key={i} variant="default" size="sm">{part}</Badge>
            ))}
          </div>
        )}

        {/* Leads table */}
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<HiOutlineSparkles className="w-8 h-8 text-gray-400" />}
            title="No matching leads"
            description="No leads currently match this smart list's filters"
          />
        ) : (
          <div className="overflow-x-auto border border-secondary-200 rounded-xl">
            <Table>
              <TableHead>
                <TableRow hoverable={false}>
                  <TableCell header className="w-10">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    />
                  </TableCell>
                  <TableCell header>Name</TableCell>
                  <TableCell header>Phone</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Stage</TableCell>
                  <TableCell header>Score</TableCell>
                  <TableCell header>Assigned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead._id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(lead._id)}
                        onChange={() => toggleSelect(lead._id)}
                        className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-gray-900">{lead.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{lead.phone}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGES[lead.status] || 'default'} size="sm">
                        {lead.status?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600 text-sm">
                        {lead.stage?.replace(/_/g, ' ') || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600 text-sm font-medium">{lead.leadScore ?? 0}</span>
                    </TableCell>
                    <TableCell>
                      {lead.assignedTo ? (
                        <span className="text-gray-700 text-sm">
                          {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                        </span>
                      ) : (
                        <Badge variant="warning" size="sm">Unassigned</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {isOpen && total > 0 && (
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs sm:text-sm text-gray-500">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SmartLists;